import axios from "axios";
import {
  getAuthHeaders,
  getRefreshToken,
  setTokens,
  clearSession
} from "../utils/authStorage";

const API_URL = process.env.VUE_APP_ROOT_API;

// -------------------------
// Singleton refresh promise
// -------------------------
let refreshPromise = null;

// -------------------------
// Logout callback (set by app)
// -------------------------
let logoutCallback = null;

// -------------------------
// Refresh token function
// -------------------------
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await axios.post(`${API_URL}/auth/refresh`, {
      refreshToken
    });

    if (!response.data?.token) return false;

    setTokens(response.data);

    return true;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return false;
  }
};

// -------------------------
// Build request config with auth headers
// -------------------------
const buildConfig = (options = {}) => {
  const authHeaders = getAuthHeaders();
  return {
    ...options,
    headers: {
      ...(authHeaders.headers || {}),
      ...(options.headers || {})
    }
  };
};

// -------------------------
// Handle 401 and refresh logic
// -------------------------
const handleUnauthorized = async () => {
  // If already refreshing, wait for that promise
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        return await refreshAccessToken();
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  const refreshed = await refreshPromise;

  if (!refreshed) {
    clearSession();
    logoutCallback?.();
    throw new Error("Unauthorized");
  }

  return true;
};

// -------------------------
// Main request function
// -------------------------
const request = async (method, url, data = null, options = {}, _retried = false) => {
  const fullUrl = API_URL + url;
  const config = buildConfig(options);

  const execute = () => {
    switch (method.toLowerCase()) {
      case "get":
        return axios.get(fullUrl, config);
      case "post":
        return axios.post(fullUrl, data, config);
      case "put":
        return axios.put(fullUrl, data, config);
      case "patch":
        return axios.patch(fullUrl, data, config);
      case "delete":
        return axios.delete(fullUrl, config);
      default:
        return axios.request({ ...config, method, url: fullUrl, data });
    }
  };

  try {
    return await execute();
  } catch (error) {
    // Check if 401 Unauthorized
    if (error.response?.status !== 401) {
      throw error;
    }

    // Already retried, give up
    if (_retried) {
      clearSession();
      logoutCallback?.();
      throw new Error("Unauthorized");
    }

    // Try to refresh and retry
    await handleUnauthorized();
    return request(method, url, data, options, true);
  }
};

// -------------------------
// Public API
// -------------------------
const ApiService = {
  setLogoutCallback(fn) {
    logoutCallback = fn;
  },

  get(url, options = {}) {
    return request("get", url, null, options);
  },

  post(url, data = {}, options = {}) {
    return request("post", url, data, options);
  },

  put(url, data = {}, options = {}) {
    return request("put", url, data, options);
  },

  patch(url, data = {}, options = {}) {
    return request("patch", url, data, options);
  },

  delete(url, options = {}) {
    return request("delete", url, null, options);
  },

  // Raw request with full response details
  async requestRaw(method, url, data = null, options = {}, _retried = false) {
    const fullUrl = API_URL + url;
    const config = buildConfig(options);

    const execute = () => {
      switch (method.toLowerCase()) {
        case "get":
          return axios.get(fullUrl, config);
        case "post":
          return axios.post(fullUrl, data, config);
        case "put":
          return axios.put(fullUrl, data, config);
        case "patch":
          return axios.patch(fullUrl, data, config);
        case "delete":
          return axios.delete(fullUrl, config);
        default:
          return axios.request({ ...config, method, url: fullUrl, data });
      }
    };

    try {
      const response = await execute();
      return {
        data: response.data,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      if (error.response?.status !== 401) {
        return {
          data: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers
        };
      }

      if (_retried) {
        clearSession();
        logoutCallback?.();
        throw new Error("Unauthorized");
      }

      await handleUnauthorized();
      return this.requestRaw(method, url, data, options, true);
    }
  }
};

export default ApiService;
